import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useReports, useWorldOptions } from '../api/queries';
import {
  ATTACK_SIZE_ICON,
  ATTACK_SIZE_LABEL,
  OUTCOME_ICON,
  SCOUT_BUILDINGS_ICON,
  SCOUT_BUILDINGS_LABEL,
  SNOB_ICON,
  SNOB_LABEL,
  attackSizeFromCount,
  hasScoutedBuildings,
  hasSnob,
  outcomeLabel,
  type Report,
  type User,
} from '../api/types';
import { ReportDetailsModal } from '../components/ReportDetailsModal';
import { Spinner } from '../components/Spinner';
import { useDebounced } from '../hooks/useDebounced';
import { formatCoords, formatDate } from '../lib/reportFormat';

/**
 * Liste aller eingelesenen Berichte einer Welt -- nach Kampfzeit sortiert
 * (neueste zuerst), mit "nur eigene" und Filtern nach Spieler, Dorf und
 * Koordinaten. Die Filter greifen jeweils auf Angreifer UND Verteidiger.
 * Eigene Seite statt Teil von "Bericht einlesen", weil sie inzwischen ein
 * eigenständiges Werkzeug ist (Paging, mehr Spalten). Berichte sind
 * read-only -- es gibt bewusst keine Löschen-Aktion, ein einmal eingelesener
 * Bericht bleibt unverändert bestehen.
 */
export function ReportsPage({ user }: { user: User }) {
  const [onlyOwn, setOnlyOwn] = useState(true);
  const [player, setPlayer] = useState('');
  const [village, setVillage] = useState('');
  const [coordinates, setCoordinates] = useState('');
  const [page, setPage] = useState(1);
  const [openReport, setOpenReport] = useState<Report | null>(null);

  // Mitglieder hängen an ihrer Welt; nur weltübergreifende Konten wählen aus.
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const debouncedPlayer = useDebounced(player);
  const debouncedVillage = useDebounced(village);
  const debouncedCoordinates = useDebounced(coordinates);

  const coordinatesValid =
    debouncedCoordinates === '' || /^\d{1,3}\s*[|,]\s*\d{1,3}$/.test(debouncedCoordinates);

  // Ein geänderter Filter kann die vorherige Seite ins Leere laufen lassen --
  // deshalb bei jeder Filteränderung zurück auf Seite 1.
  useEffect(() => {
    setPage(1);
  }, [onlyOwn, debouncedPlayer, debouncedVillage, debouncedCoordinates, activeWorld]);

  const reports = useReports({
    welt: needsWorldChoice ? worldKey : undefined,
    eigene: onlyOwn,
    ...(debouncedPlayer ? { spieler: debouncedPlayer } : {}),
    ...(debouncedVillage ? { dorf: debouncedVillage } : {}),
    ...(coordinatesValid && debouncedCoordinates ? { koordinaten: debouncedCoordinates } : {}),
    seite: page,
  });

  const hasGamePlayer = reports.data?.hasGamePlayer ?? Boolean(user.gamePlayerId ?? user.gamePlayerName);
  const rows = reports.data?.reports ?? [];
  const total = reports.data?.total ?? 0;
  const pageSize = reports.data?.pageSize ?? 25;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Berichte</h1>
          <p className="muted">Neueste zuerst, nach Kampfzeit sortiert.</p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => reports.refetch()}
          disabled={reports.isFetching}
        >
          {reports.isFetching ? 'Lädt …' : 'Aktualisieren'}
        </button>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && (
        <p className="muted center pad">Wähle eine Welt, um ihre Berichte zu sehen.</p>
      )}

      {activeWorld && (
        <>
          <div className="filter-bar">
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={onlyOwn}
                onChange={(event) => setOnlyOwn(event.target.checked)}
              />
              <span>Nur eigene Berichte anzeigen</span>
            </label>
            <input
              type="text"
              placeholder="Spieler"
              value={player}
              onChange={(event) => setPlayer(event.target.value)}
            />
            <input
              type="text"
              placeholder="Dorf"
              value={village}
              onChange={(event) => setVillage(event.target.value)}
            />
            <input
              type="text"
              placeholder="Koordinaten (500|500)"
              value={coordinates}
              onChange={(event) => setCoordinates(event.target.value)}
            />
          </div>

          {onlyOwn && !hasGamePlayer && (
            <div className="flash flash-info">
              Ohne hinterlegte Spielfigur lassen sich keine eigenen Berichte erkennen. Trage sie
              unter <Link to="/konto">Mein Konto</Link> ein, oder schalte den Haken oben aus.
            </div>
          )}

          {reports.isPending && <Spinner label="Berichte werden geladen …" />}

          {reports.isError && (
            <div className="flash flash-error">
              {reports.error instanceof ApiError
                ? reports.error.message
                : 'Berichte konnten nicht geladen werden.'}
            </div>
          )}

          {!reports.isPending && rows.length === 0 && (
            <p className="muted center pad">Keine Berichte gefunden.</p>
          )}

          {rows.length > 0 && (
            <>
              <div className={`table-wrap ${reports.isFetching ? 'is-refreshing' : ''}`}>
                <table className="table">
                  <thead>
                    <tr>
                      <th />
                      <th />
                      <th />
                      <th />
                      <th>Name</th>
                      <th>Angreifer</th>
                      <th>Herkunft</th>
                      <th>Verteidiger</th>
                      <th>Ziel</th>
                      <th>Kampf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((report) => (
                      <ReportRow key={report.id} report={report} onOpen={() => setOpenReport(report)} />
                    ))}
                  </tbody>
                </table>
              </div>

              {openReport && (
                <ReportDetailsModal report={openReport} onClose={() => setOpenReport(null)} />
              )}

              <div className="pager">
                <span className="muted">
                  Seite {page} von {pageCount} — {total.toLocaleString('de-DE')} Berichte
                </span>
                <div className="button-row">
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={page <= 1 || reports.isFetching}
                    onClick={() => setPage((current) => current - 1)}
                  >
                    ‹ Zurück
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    disabled={page >= pageCount || reports.isFetching}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    Weiter ›
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function ReportRow({ report, onOpen }: { report: Report; onOpen: () => void }) {
  const attackSize = attackSizeFromCount(report.attackerUnitCount);

  return (
    <tr>
      <td>
        {attackSize && (
          <img
            src={ATTACK_SIZE_ICON[attackSize]}
            alt={ATTACK_SIZE_LABEL[attackSize]}
            title={ATTACK_SIZE_LABEL[attackSize]}
            className="report-icon"
          />
        )}
      </td>
      <td>
        {report.viewerOutcome?.outcome && (
          <img
            src={OUTCOME_ICON[report.viewerOutcome.outcome]}
            alt={outcomeLabel(report.viewerOutcome.side, report.viewerOutcome.outcome)}
            title={outcomeLabel(report.viewerOutcome.side, report.viewerOutcome.outcome)}
            className="report-icon"
          />
        )}
      </td>
      <td>
        {hasScoutedBuildings(report) && (
          <img
            src={SCOUT_BUILDINGS_ICON}
            alt={SCOUT_BUILDINGS_LABEL}
            title={SCOUT_BUILDINGS_LABEL}
            className="report-icon"
          />
        )}
      </td>
      <td>
        {hasSnob(report) && (
          <img src={SNOB_ICON} alt={SNOB_LABEL} title={SNOB_LABEL} className="report-icon" />
        )}
      </td>
      <td className="muted">
        <button type="button" className="link-button" onClick={onOpen}>
          {report.name ?? '—'}
        </button>
      </td>
      <td>{report.attacker.player ?? '—'}</td>
      <td className="nowrap">{formatCoords(report.attacker)}</td>
      <td>{report.defender.player ?? '—'}</td>
      <td className="nowrap">{formatCoords(report.defender)}</td>
      <td className="nowrap">{formatDate(report.battleAt)}</td>
    </tr>
  );
}
